const ENABLE_TRANSFERS_KEYS = ['enable_transfers', 'enable-transfers'] as const

const getConfigValue = (value: unknown): unknown => {
  if (value && typeof value === 'object' && 'value' in value) {
    return (value as { value: unknown }).value
  }

  return value
}

const configValueIsTrue = (value: unknown): boolean => {
  const unwrapped = getConfigValue(value)
  if (unwrapped === true || unwrapped === 1) return true
  if (typeof unwrapped === 'string') {
    const normalized = unwrapped.trim().toLowerCase()
    return normalized === 'true' || normalized === '1'
  }

  return false
}

/**
 * Whether inventory transfer features are enabled for the active business.
 * @param inventoryConfigs - Inventory group from business configurations.
 * @returns True when `enable_transfers` (or hyphenated alias) is truthy.
 */
export const areInventoryTransfersEnabled = (inventoryConfigs: Record<string, unknown>): boolean =>
  ENABLE_TRANSFERS_KEYS.some((key) => configValueIsTrue(inventoryConfigs[key]))

/**
 * Whether a named inventory configuration flag is enabled.
 * @param inventoryConfigs - Inventory group from business configurations.
 * @param key - Config key (underscore form preferred; hyphen alias checked automatically).
 * @returns True when the flag is truthy.
 */
export const isInventoryConfigEnabled = (inventoryConfigs: Record<string, unknown>, key: string): boolean => {
  const aliases = key.includes('_') ? [key, key.replace(/_/g, '-')] : [key, key.replace(/-/g, '_')]
  return aliases.some((alias) => configValueIsTrue(inventoryConfigs[alias]))
}

/**
 * Whether partial transfer receipts are allowed for the active business.
 * When false, every line must be received at full dispatched quantity.
 * @param inventoryConfigs - Inventory group from business configurations.
 * @returns True when `allow_partial_transfer` (or hyphenated alias) is truthy.
 */
export const arePartialTransfersAllowed = (inventoryConfigs: Record<string, unknown>): boolean =>
  isInventoryConfigEnabled(inventoryConfigs, 'allow_partial_transfer')
